import { GridLegacy } from "@mui/material";
import clsx from "clsx";
import { useAtom, useAtomValue, useStore } from "jotai";
import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { currentTheme, refreshSectionDataAtom, selectedSectionAtom } from "../../../atoms";
import SubHeader from "../NotWorkArea(Side&Head)/Header/SubHeader";
import LoadingMask from "../NotWorkArea(Side&Head)/LoadingMask";
import OutPut from "../NotWorkArea(Side&Head)/OutPut";
import type { SectionsList, SelectedSection } from "../NotWorkArea(Side&Head)/SideBar/SideBarInterface";
import SmetaSplitter from "../Tools/Splitter/SmetaSplitter";
import SmetaSplitterItem from "../Tools/Splitter/SmetaSplitterItem";
import { SmetaTabItem } from "../Tools/Tabs/PinIcons";
import { SmetaResizeElement } from "../../ComponentInterface";
import SmetaTabs from "../Tools/Tabs/SmetaTabs";
import { getSectionByID, updateGridData } from "../Tools/Tools";
import { contentMaker } from "./FullRightSideData";

// вкладка секции (SmetaTabItem или заглушка загрузки), ID секции лежит в data.ID
type SectionTab = ReactElement<{ data: { ID: string } }>;

const hasTab = (tabs: SectionTab[], id: string) => tabs.some((tab) => tab.props.data.ID === id);

// eslint-disable-next-line @typescript-eslint/naming-convention
export default function FullRightSide(props: any) {
  const store = useStore();

  //массив с секциями
  const [sections, setSections] = useState<SectionTab[]>([]);

  // секция, вкладка которой показана. Индекс вычисляется по ней, а не хранится: вкладки добавляются
  // асинхронно, и сохранённый индекс успевает устареть
  const [activeSectionId, setActiveSectionId] = useState<string>();
  const tabIndex = Math.max(
    0,
    sections.findIndex((tab) => tab.props.data.ID === activeSectionId),
  );

  //переменная для ререндера при перерисовке компонента
  const sideBarSelected = useAtomValue(selectedSectionAtom);

  // переменная инициирующая обновление
  const [refreshDataAtom, setRefreshDataAtom] = useAtom(refreshSectionDataAtom);

  const theme = useAtomValue(currentTheme);

  //при смене id секции вызывается функция создания секций
  useEffect(() => {
    if (sideBarSelected.ID !== "") addTab();
  }, [sideBarSelected.ID]);

  //при вызове функции updateSection меняется объект в store что вызывает сработку этого хука
  useEffect(() => {
    if (refreshDataAtom && refreshDataAtom.refresh) {
      //отрисовка в секции по указанному id загрузки
      updateMainSection(true, refreshDataAtom.refreshId, refreshDataAtom.CommitFormOnSection).then(() => {
        setTimeout(() => {
          updateMainSection(false, refreshDataAtom.refreshId, refreshDataAtom.CommitFormOnSection);
          setRefreshDataAtom(null);
        }, 100);
      });
    }
  }, [refreshDataAtom]);

  useEffect(() => {
    document.querySelectorAll("[data-smetaresizer]").forEach((el) => {
      if (el.checkVisibility() && typeof (el as SmetaResizeElement)?.resize === "function") {
        (el as SmetaResizeElement)?.resize();
      }
    });
    updateGridData(0);
  }, [tabIndex]);

  const singleTab = async (section: SelectedSection | SectionsList, commitFormOnSection?: unknown) => {
    return (
      <SmetaTabItem key={section.ID} data={{ ...section }}>
        {await contentMaker(section, commitFormOnSection)}
      </SmetaTabItem>
    );
  };

  //функция создает секцию если она ранее не была создана, в противном случае показывает вкладку выбранной секции
  async function addTab() {
    const section = sideBarSelected;
    //если секции нет
    if (!hasTab(sections, section.ID)) {
      //добавление выбранной  секции
      const newTab = await singleTab(section);
      // пока вкладка собиралась, в списке могли появиться другие, поэтому дополняем актуальный список (а не sections
      // из замыкания). Повторная сборка той же секции (быстрые клики, StrictMode) не должна давать дубликат
      setSections((tabs) => (hasTab(tabs, section.ID) ? tabs : [...tabs, newTab]));
      // если за это время выбрали другую секцию, то вкладка остаётся в списке, но не показывается
      if (store.get(selectedSectionAtom).ID !== section.ID) return;
    }
    setActiveSectionId(section.ID);
  }

  async function updateMainSection(load: boolean, id?: string, commitFormOnSection?: string) {
    // обновляется выбранная секция или любая другая (сервер может обновить форму невыбранной секции)
    const section = id && id !== sideBarSelected.ID ? getSectionByID(id) : sideBarSelected;
    if (!section) return;

    //костыль для VirtualDOM - нужно перерисовать секцию, но дом ее оставляет прежней.
    const content = load ? <LoadingMask data={{ ID: section.ID }} /> : await singleTab(section, commitFormOnSection);

    // вкладку ищем в актуальном списке, т.к. пока собирался контент, он мог измениться
    setSections((tabs) => {
      //если секции не существует то она отрисуется скрытой, показанная вкладка при этом не меняется
      if (!hasTab(tabs, section.ID)) return load ? tabs : [...tabs, content];
      //удаляем старую секцию/загрузку и вставляем обновленную секцию
      return tabs.map((tab) => (tab.props.data.ID === section.ID ? content : tab));
    });
  }

  return (
    <GridLegacy
      container
      direction="column"
      justifyContent="center"
      sx={{ flexGrow: 1 }}
      alignItems="stretch"
      style={{}}
      id="IDTEST"
      className={clsx("backgroundColorStimate", { light: theme === "light" })}
    >
      <SmetaSplitter
        id={"OutPutTextSplitter"}
        onResizeEnd={() => updateGridData(0)}
        onCollapse={() => {
          updateGridData(0);
        }}
        onExpand={() => {
          updateGridData(0);
        }}
        orientation="horizontal"
        style={{
          width: "100%",
          height: "100%",
        }}
        primaryIndex="start"
        transitionDuration={250}
      >
        <SmetaSplitterItem>
          <GridLegacy item style={{ width: "100%", height: "100%" }}>
            <GridLegacy
              style={{ width: "100%", height: "100%" }}
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="stretch"
            >
              <SubHeader drawerOpen={props.drawerBool} />
              <SmetaTabs position="hidden" tabIndex={tabIndex} style={{ flex: "1 1 auto", width: "100%" }}>
                {sections}
              </SmetaTabs>
            </GridLegacy>
          </GridLegacy>
        </SmetaSplitterItem>
        <SmetaSplitterItem defaultCollapsed={true} size="100px">
          <div style={{ width: "100%", height: "100%" }}>
            {sideBarSelected.ID === undefined || props.isLoading ? <></> : <OutPut></OutPut>}
          </div>
        </SmetaSplitterItem>
      </SmetaSplitter>
    </GridLegacy>
  );
}
